import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  List,
  Drawer,
} from "@mui/material";
import {
  Notifications,
  AccountCircle,
  Person,
  Settings,
  Logout,
  Close,
} from "@mui/icons-material";
import en_ja from "../translations/en_ja";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Safe parse of user from localStorage
  const user = React.useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // check super admin
  const roleName = user?.role?.roleName;
  const isSuperAdmin =
    typeof roleName === "string" && roleName.trim().toLowerCase() === "super admin";

  const isDashboard =
    location.pathname === "/" || location.pathname === "/dashboard";

  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const isProfileMenuOpen = Boolean(profileAnchorEl);

  const handleProfileClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setProfileAnchorEl(null);
  };

  const handleProfileMenuClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleProfileClose();
    navigate("/profile");
  };

  const handleSettingsClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleProfileClose();

    navigate(isSuperAdmin ? "/super-admin-settings" : "/settings");
  };

  const handleLogout = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleProfileClose();
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.clear();
    navigate("/login");
  };

  const getPageTitle = () => {
    const pathname = location.pathname;
    const route = pathname.split("/")[1];
    if (!route) return { title: "", subTitle: "" };

    switch (route) {
      case "dashboard":
        return {
          title: `${en_ja["Welcome, Admin"]} ${user?.username || ""}!`,
          subTitle: en_ja["Here's Your Clinic Management Overview For Today"],
        };
      case "clinic-management":
        return {
          title: en_ja["Clinic Management"],
          subTitle: en_ja["Manage and View all Clinic information"],
        };
      case "user-management":
        return {
          title: en_ja["User Management"],
          subTitle: en_ja["Manage and View all User information"],
        };
      case "reports":
        return {
          title: en_ja["Reports"],
          subTitle: en_ja["Manage and View all Reports"],
        };
      case "products":
        return {
          title: en_ja["Products"],
          subTitle: en_ja["Manage and View all Products"],
        };
      case "orders":
        return {
          title: en_ja["Orders"],
          subTitle: en_ja["Manage and View all Orders"],
        };
      case "pharmacy-management":
        return {
          title: en_ja["Pharmacy Management"],
          subTitle: en_ja["Manage and monitor all registered Pharmacies"],
        };
      case "coupon-management":
        return {
          title: en_ja["Coupon Management"],
          subTitle: en_ja["Manage your discount campaigns"],
        };
      case "stores":
        return {
          title: en_ja["Stores"],
          subTitle: en_ja["Manage your physical store locations"],
        };
      case "super-admin-settings":
        return {
          title: en_ja["Settings"],
          subTitle: en_ja["Manage and update your account settings"],
        };
      case "settings":
        return {
          title: en_ja["Settings"],
          subTitle: en_ja["Manage and update your account settings"],
        };
      case "profile":
        return {
          title: en_ja["Profile"],
          subTitle: en_ja["Manage and view your profile information"],
        };
      case "notifications":
        return {
          title: en_ja["Notifications"],
          subTitle: en_ja["Manage your Notifications"],
        };
      case "banner-management":
        return {
          title: en_ja["Banner Management"],
          subTitle: en_ja["Manage your Banner"],
        };
      case "warranty-management":
        return {
          title: en_ja["Warranty Management"],
          subTitle: en_ja["Manage Warranty Management"],
        };

      case "deeplink-management":
        return {
          title: en_ja["Deeplink Management"],
          subTitle: en_ja["Create and manage deep links for app navigation"],
        };

      // <-- Admin Management ONLY for Super Admin -->
      case "admin-management":
        if (isSuperAdmin) {
          return {
            title: en_ja["Admin Management"] ?? "Admin Management",
            subTitle: en_ja["Manage admin and permissions"] ?? "Manage admin and permissions",
          };
        }
        return { title: "", subTitle: "" };

      default:
        return { title: "", subTitle: "" };
    }
  };

  const pageTitle = getPageTitle();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  // Dummy notifications
  const notifications = [
    {
      id: 1,
      message: en_ja["Clinic schedule for today is updated. Check the new appointment slots"],
    },
    {
      id: 2,
      message: en_ja["A product is out of stock. Please update the inventory or notify the relevant team"],
    },
    {
      id: 3,
      message: en_ja["Alert: Appointment with 'David Brown' has been rescheduled to 4:00 PM due to doctor unavailability"],
    },
  ];

  const handleCloseDialog = () => {
    setIsNotificationOpen(false);
  };

  const handleClearAll = () => {
    console.log(en_ja["Clear All clicked"]);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          py: 3,
          px: 4,
          mx: 2,
          mt: 1,
          borderRadius: "32px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)",
          background: "linear-gradient(145deg, #f9f9f9 0%, #f5f5f5 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.2)",
          position: "relative",
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ ml: 2 }}>
              <Typography
                variant="h4"
                sx={{
                  color: "#000",
                  fontWeight: "bold",
                  mb: 0.5,
                  fontSize: "32px",
                  letterSpacing: "-0.02em",
                }}
              >
                {isDashboard
                  ? `${en_ja["Welcome,"]}, ${user?.username || ""}!`
                  : pageTitle.title}
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="body1"
                sx={{ color: "#666666", fontSize: "16px" }}
              >
                {isDashboard
                  ? en_ja["Here's Your Clinic Management Overview For Today"]
                  : pageTitle.subTitle}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              padding: "8px 16px",
              borderRadius: "12px",
              background:
                "linear-gradient(135deg, rgba(118,174,222,0.08) 0%, rgba(161,195,232,0.12) 100%)",
              border: "1px solid rgba(118,174,222,0.1)",
            }}
          >
            {/* <IconButton
                  onClick={() => setIsNotificationOpen(true)}
                  sx={{
                    color: "#6b7280",
                    background: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "10px",
                    padding: "8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(118, 174, 222, 0.1)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(118, 174, 222, 0.2)",
                      color: "#76AEDE",
                    },
                  }}
                >
                  <Badge badgeContent={3} color="error">
                    <Notifications />
                  </Badge>
                </IconButton> */}
            <IconButton
              onClick={handleProfileClick}
              sx={{
                p: 0,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "transparent",
                  transform: "translateY(-2px)",
                  "& .MuiAvatar-root": {
                    boxShadow: "0 6px 20px rgba(118,174,222,0.3)",
                    transform: "scale(1.05)",
                  },
                },
              }}
            >
              <Avatar
                {...(user?.profile_image && { src: user.profile_image })}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: "#76AEDE",
                  fontSize: "14px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 3px 12px rgba(118,174,222,0.2)",
                  border: "2px solid rgba(255,255,255,0.3)",
                }}
              >
                {!user?.profile_image && <AccountCircle />}
              </Avatar>
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={profileAnchorEl}
        open={isProfileMenuOpen}
        onClose={handleProfileClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            borderRadius: "12px",
            minWidth: 180,
            zIndex: 9999,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        sx={{ zIndex: 9999 }}
      >
        <MenuItem
          onClick={handleProfileMenuClick}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: "rgba(118, 174, 222, 0.08)",
            },
          }}
        >
          <ListItemIcon>
            <Person fontSize="small" sx={{ color: "#76AEDE" }} />
          </ListItemIcon>
          <ListItemText>{en_ja["Profile"]}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleSettingsClick}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: "rgba(118, 174, 222, 0.08)",
            },
          }}
        >
          <ListItemIcon>
            <Settings fontSize="small" sx={{ color: "#76AEDE" }} />
          </ListItemIcon>
          <ListItemText>{en_ja["Settings"]}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.08)",
            },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: "#f44336" }} />
          </ListItemIcon>
          <ListItemText sx={{ color: "#f44336" }}>
            {en_ja["Logout"]}
          </ListItemText>
        </MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={isNotificationOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            width: "400px",
            maxWidth: "100%",
            borderTopLeftRadius: "16px",
            borderBottomLeftRadius: "16px",
            p: 2,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {en_ja["Notification"]}
          </Typography>
          <IconButton onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Box
            sx={{
              flex: 1,
              textAlign: "center",
              py: 1,
              borderRadius: "8px",
              backgroundColor: "#f5f5f5",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {en_ja["UNREAD"]}
          </Box>
          <Box
            sx={{
              flex: 1,
              textAlign: "center",
              py: 1,
              borderRadius: "8px",
              backgroundColor: "#fff",
              fontWeight: "bold",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            {en_ja["ALL"]}
          </Box>
        </Box>

        <Box textAlign="end" mb={2}>
          <Typography
            onClick={handleClearAll}
            sx={{
              cursor: "pointer",
              fontSize: "0.875rem",
              color: "#6b7280",
              "&:hover": { color: "#000" },
            }}
          >
            {en_ja["Clear All"]}
          </Typography>
        </Box>

        <List sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          {notifications.map((n) => (
            <Box
              key={n.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                p: 2,
                mb: 1,
                borderRadius: "12px",
                backgroundColor: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                "&:hover": {
                  backgroundColor: "rgba(220,53,69,0.04)",
                  color: "#dc3545",
                },
              }}
            >
              <Avatar sx={{ bgcolor: "#f5f5f5", width: 36, height: 36 }}>
                <Notifications sx={{ color: "#666", fontSize: 20 }} />
              </Avatar>
              <Typography variant="body2" sx={{ color: "#444" }}>
                {n.message}
              </Typography>
            </Box>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Header;
